// 📄 pages/Admin/sections/EscenarioPruebaCatalogSection.tsx
import { useEffect, useState } from 'react';
import { Plus, RefreshCw, FlaskConical, Pencil, Trash2 } from 'lucide-react';
import { EscenarioPrueba, getEscenariosPrueba, deleteEscenarioPrueba } from '../../../services/catalogoCasosPruebaService.ts';
import { SOLUCIONES } from '../../Proyectos/solucionesConfig.ts';
import EscenarioPruebaFormModal from '../components/EscenarioPruebaFormModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

export default function EscenarioPruebaCatalogSection() {
  const [escenarios, setEscenarios] = useState<EscenarioPrueba[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<EscenarioPrueba | null>(null);
  const [aEliminar, setAEliminar] = useState<EscenarioPrueba | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      setEscenarios(await getEscenariosPrueba());
    } catch (err) {
      console.error('Error cargando el catálogo de escenarios de prueba:', err);
      setError('No se pudo cargar el catálogo de escenarios de prueba.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const openCreate = () => { setEditando(null); setModalOpen(true); };
  const openEdit = (e: EscenarioPrueba) => { setEditando(e); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); cargar(); };

  const handleEliminar = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await deleteEscenarioPrueba(aEliminar.id);
      setAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar el escenario:', err);
    } finally {
      setEliminando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-white">Biblioteca de Casos de Prueba</h1>
          <p className="text-sm text-gray-400 mt-1">Escenarios sugeridos por solución/canal — el equipo consultor los clona a un proyecto puntual desde "Casos de Prueba".</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all shadow-lg"
        >
          <Plus className="w-4 h-4" />
          Nuevo escenario
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">Cargando catálogo...</p>
        </div>
      ) : escenarios.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <FlaskConical className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no hay escenarios sugeridos en la biblioteca.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {escenarios.map((e) => (
            <div key={e.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-white">{e.nombre}</h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {SOLUCIONES[e.solucion]?.label || e.solucion}{e.canalNombre ? ` · ${e.canalNombre}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => openEdit(e)} className="text-gray-400 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setAEliminar(e)} className="text-gray-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {e.campos.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {e.campos.map((campo, i) => (
                    <span key={i} className="text-xs px-2 py-1 rounded-lg bg-black/20 border border-white/10 text-gray-300">
                      <span className="text-gray-500">{campo.nombre}:</span> {campo.valor}
                    </span>
                  ))}
                </div>
              )}
              {e.detalles && <p className="text-sm text-gray-300 whitespace-pre-wrap">{e.detalles}</p>}
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <EscenarioPruebaFormModal escenario={editando} onClose={() => setModalOpen(false)} onSaved={handleSaved} />
      )}

      {aEliminar && (
        <ConfirmDialog
          title="¿Eliminar este escenario de la biblioteca?"
          message={`"${aEliminar.nombre}" se eliminará de la biblioteca global. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? 'Eliminando...' : 'Eliminar'}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setAEliminar(null)}
        />
      )}
    </div>
  );
}
